import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject } from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { merge, of, shareReplay, take } from 'rxjs';
import { distinctUntilChanged, filter, map, skip, switchMap } from 'rxjs/operators';
import {
  ActivitySectionDirective,
  CallWaiterService,
  CORE_FEATURE_TOGGLE,
  InteractionService,
  InteractsPresentComponent,
  UserActivityClickTrackingDirective,
} from '@core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { CallWaiterIndicatorComponent } from '@app/components/call-waiter-indicator/call-waiter-indicator.component';

@Component({
  selector: 'app-tab-bar',
  templateUrl: './tab-bar.component.html',
  styleUrl: './tab-bar.component.scss',
  imports: [
    RouterLink,
    ActivitySectionDirective,
    UserActivityClickTrackingDirective,
    InteractsPresentComponent,
    CallWaiterIndicatorComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TabBarComponent {

  private readonly featureToggle = inject(CORE_FEATURE_TOGGLE);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly interaction = inject(InteractionService);
  private readonly callWaiterService = inject(CallWaiterService);

  private readonly currentUrl$ = merge(
    of(this.router.url),
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
    ),
  ).pipe(
    distinctUntilChanged(),
    shareReplay(1),
  );

  private readonly pageIs$ = this.currentUrl$.pipe(
    map((url) => {
      const result = {
        home: false,
        menu: false,
        reserveTable: false,
        service: false,
        more: false,
      };

      if (url === '/main/home') {
        result.home = true;
      }
      else if (url === '/main/menu') {
        result.menu = true;
      }
      else if (url === '/main/reserve-table') {
        result.reserveTable = true;
      }
      else if (url.startsWith('/main/service')) {
        result.service = true;
      }
      else if (url === '/main/more') {
        result.more = true;
      }

      return result;
    }),
    shareReplay(1),
  );

  public readonly pageIs = toSignal(this.pageIs$, {
    requireSync: true,
  });

  public readonly isIdle = toSignal(this.interaction.idle$, {
    requireSync: true,
  });

  public readonly hideTabBar = computed(() => {
    if (this.featureToggle.tabBarAutoHide) {
      return this.isIdle();
    }

    return false;
  });

  public readonly callWaiterMain = toSignal(this.callWaiterService.mainStatus$, {
    initialValue: true,
  });

  public readonly callWaiterRequestBill = toSignal(this.callWaiterService.secondaryStatus$, {
    requireSync: true,
  });

  constructor() {
    this.interaction.idle$.pipe(
      distinctUntilChanged(),
      skip(1),
      switchMap((idle) => this.pageIs$.pipe(
        take(1),
        map((pageIs) => {
          return { idle, isHomePage: pageIs.home };
        }),
      )),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(({ idle, isHomePage }) => {
      if (!idle) {
        if (isHomePage) {
          this.router.navigate(this.featureToggle.idleClickGoTo).then();
        }
      }
      else {
        if (!isHomePage) {
          this.router.navigate(['/main/home']).then();
        }

        const currentLang = this.translate.getCurrentLang();
        const fallbackLang = this.translate.getFallbackLang();

        if (fallbackLang && currentLang !== fallbackLang) {
          this.translate.use(fallbackLang);
        }
      }
    });
  }

}
